import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AuthContext } from './context/AuthContext';
import { initiateSocket, getSocket } from './socket';
import axiosInstance from './axiosInstance';
import ProtectedComponent from './ProtectedComponent';
import confetti from 'canvas-confetti';
import solumGuyImage from './images/solum-guy.png';
import solumGalImage from './images/solum-gal.png';
import yaySound from './images/yay.mp3';
import './styles/WebhookMonitor.css';

// Audio unlock state - persists across re-renders
let audioUnlocked = false;
let audioInstance = null;

function WebhookMonitor() {
  const { user } = useContext(AuthContext);
  const [webhooks, setWebhooks] = useState([]);
  const [connected, setConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationImage, setCelebrationImage] = useState(solumGuyImage);
  const terminalRef = useRef(null);

  // Unlock audio on first user interaction
  useEffect(() => {
    if (audioUnlocked) return;

    const unlockAudio = () => {
      if (audioUnlocked) return;

      // Create and play silent audio to unlock
      audioInstance = new Audio(yaySound);
      audioInstance.volume = 0;
      audioInstance.play().then(() => {
        audioInstance.pause();
        audioInstance.currentTime = 0;
        audioInstance.volume = 0.7;
        audioUnlocked = true;
        console.log('[WebhookMonitor] Audio unlocked via user interaction');
      }).catch(() => {});

      // Remove listeners after first interaction
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Fire confetti celebration for button presses
  const triggerCelebration = useCallback((webhookBody) => {
    // Search entire body for "leslie" (case insensitive)
    const bodyStr = JSON.stringify(webhookBody).toLowerCase();
    const isLeslie = bodyStr.includes('leslie');

    console.log('[WebhookMonitor] Celebration - isLeslie:', isLeslie, 'body contains:', bodyStr.substring(0, 200));

    setCelebrationImage(isLeslie ? solumGalImage : solumGuyImage);
    setShowCelebration(true);

    // Play celebration sound using pre-loaded instance if available
    if (audioUnlocked && audioInstance) {
      audioInstance.currentTime = 0;
      audioInstance.play().catch(e => console.log('Audio play failed:', e));
    } else {
      // Fallback: try creating new audio
      const audio = new Audio(yaySound);
      audio.volume = 0.7;
      audio.play().catch(e => console.log('Audio play blocked:', e));
    }

    // Fire confetti from both sides
    const duration = 3000;
    const end = Date.now() + duration;

    const fireConfetti = () => {
      confetti({
        particleCount: 7,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: ['#00ff88', '#00bfff', '#ff6b6b', '#ffd93d', '#6bcb77']
      });
      confetti({
        particleCount: 7,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: ['#00ff88', '#00bfff', '#ff6b6b', '#ffd93d', '#6bcb77']
      });

      if (Date.now() < end) {
        requestAnimationFrame(fireConfetti);
      }
    };

    fireConfetti();

    // Hide celebration after animation
    setTimeout(() => {
      setShowCelebration(false);
    }, 3500);
  }, []);

  // Get the webhook URL to display to user
  const webhookUrl = `${window.location.origin}/api/webhook-test/receive`;

  // WebSocket setup - simple pattern like DoorFunctions (no rooms)
  useEffect(() => {
    loadHistory();
    initiateSocket();
    const socket = getSocket();

    if (!socket) {
      console.warn('[WebhookMonitor] Socket not available');
      return;
    }

    setConnected(socket.connected);

    // Handler for incoming webhooks
    const handleWebhookReceived = (data) => {
      console.log('[WebhookMonitor] webhook:received', data);
      setWebhooks(prev => [data, ...prev].slice(0, 100));

      // Check for button press - trigger celebration
      const eventType = data.body?.type || data.body?.event || data.body?.eventType || '';
      if (eventType.toLowerCase().includes('button') || eventType.toLowerCase().includes('press')) {
        triggerCelebration(data.body);
      }
    };

    const handleWebhookCleared = () => {
      setWebhooks([]);
    };

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('webhook:received', handleWebhookReceived);
    socket.on('webhook:cleared', handleWebhookCleared);

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('webhook:received', handleWebhookReceived);
      socket.off('webhook:cleared', handleWebhookCleared);
    };
  }, [triggerCelebration]);

  // Auto-scroll to top when new webhooks arrive
  useEffect(() => {
    if (autoScroll && terminalRef.current) {
      terminalRef.current.scrollTop = 0;
    }
  }, [webhooks, autoScroll]);

  const loadHistory = async () => {
    try {
      const { data } = await axiosInstance.get('/webhook-test/history?limit=50');
      if (data.success) {
        setWebhooks(data.webhooks);
      }
    } catch (err) {
      console.error('Failed to load webhook history:', err);
    }
  };

  const clearHistory = async () => {
    try {
      await axiosInstance.delete('/webhook-test/history');
      setWebhooks([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimestamp = (iso) => {
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const getEventType = (webhook) => {
    // Try to extract event type from body
    if (webhook.body?.event) return webhook.body.event;
    if (webhook.body?.type) return webhook.body.type;
    if (webhook.body?.eventType) return webhook.body.eventType;
    if (webhook.body?.data?.type) return webhook.body.data.type;
    return 'WEBHOOK';
  };

  const getEventColor = (eventType) => {
    const type = eventType.toLowerCase();
    if (type.includes('button') || type.includes('press')) return '#00ff00';
    if (type.includes('status')) return '#00bfff';
    if (type.includes('error') || type.includes('fail') || type.includes('timeout')) return '#ff4444';
    if (type.includes('success')) return '#44ff44';
    if (type.includes('processing')) return '#ffaa00';
    return '#888888';
  };

  const filteredWebhooks = webhooks.filter(wh => {
    if (!filter) return true;
    const searchStr = JSON.stringify(wh).toLowerCase();
    return searchStr.includes(filter.toLowerCase());
  });

  return (
    <ProtectedComponent functionCode="SA07" functionName="Webhook Monitor">
    <div className="webhook-monitor">
      {/* Button Press Celebration Overlay */}
      {showCelebration && (
        <div className="celebration-overlay">
          <div className="celebration-content">
            <img
              src={celebrationImage}
              alt="Celebration"
              className="celebration-image"
            />
            <p className="celebration-text">Button pressed, cause Solums the best!</p>
          </div>
        </div>
      )}

      <div className="webhook-header">
        <h2>Webhook Monitor</h2>
        <div className="webhook-status">
          <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`}></span>
          {connected ? 'Live' : 'Disconnected'}
        </div>
      </div>

      <div className="webhook-config">
        <div className="config-row">
          <label>Webhook URL:</label>
          <div className="url-display">
            <code>{webhookUrl}</code>
            <button
              className="copy-btn"
              onClick={() => copyToClipboard(webhookUrl)}
              title="Copy URL"
            >
              Copy
            </button>
          </div>
        </div>
        <p className="config-hint">
          Configure this URL in your SOLUM SaaS Webhook Subscription settings
        </p>
      </div>

      <div className="webhook-controls">
        <input
          type="text"
          placeholder="Filter webhooks..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="filter-input"
        />
        <label className="auto-scroll-toggle">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
          />
          Auto-scroll
        </label>
        <button onClick={loadHistory} className="refresh-btn">
          Refresh
        </button>
        <button onClick={clearHistory} className="clear-btn">
          Clear
        </button>
      </div>

      <div className="webhook-terminal" ref={terminalRef}>
        {filteredWebhooks.length === 0 ? (
          <div className="terminal-empty">
            <span className="prompt">&gt;</span> Waiting for webhooks...
            <span className="cursor">_</span>
          </div>
        ) : (
          filteredWebhooks.map((webhook) => {
            const eventType = getEventType(webhook);
            const isExpanded = expandedIds.has(webhook.id);

            return (
              <div
                key={webhook.id}
                className={`webhook-entry ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleExpand(webhook.id)}
              >
                <div className="webhook-summary">
                  <span className="timestamp">[{formatTimestamp(webhook.timestamp)}]</span>
                  <span
                    className="event-type"
                    style={{ color: getEventColor(eventType) }}
                  >
                    {eventType}
                  </span>
                  <span className="preview">
                    {JSON.stringify(webhook.body).substring(0, 80)}
                    {JSON.stringify(webhook.body).length > 80 ? '...' : ''}
                  </span>
                  <span className="expand-hint">{isExpanded ? '▼' : '▶'}</span>
                </div>

                {isExpanded && (
                  <div className="webhook-details">
                    <div className="detail-section">
                      <div className="detail-header">
                        Headers
                        <button
                          className="copy-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(JSON.stringify(webhook.headers, null, 2));
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <pre>{JSON.stringify(webhook.headers, null, 2)}</pre>
                    </div>
                    <div className="detail-section">
                      <div className="detail-header">
                        Body
                        <button
                          className="copy-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(JSON.stringify(webhook.body, null, 2));
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <pre>{JSON.stringify(webhook.body, null, 2)}</pre>
                    </div>
                    {Object.keys(webhook.query || {}).length > 0 && (
                      <div className="detail-section">
                        <div className="detail-header">Query Params</div>
                        <pre>{JSON.stringify(webhook.query, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="webhook-footer">
        <span>{filteredWebhooks.length} webhook(s)</span>
        {filter && <span> (filtered from {webhooks.length})</span>}
      </div>
    </div>
    </ProtectedComponent>
  );
}

export default WebhookMonitor;
